import { alpha, useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import { useListContext } from 'react-admin';
import { FocusEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { TURN_STYLES } from '@/styles/theme';

const codeSx = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: '0.85em',
  bgcolor: 'action.hover',
  px: 0.5,
  borderRadius: 0.5,
} as const;

const AdvancedSearchHelp = ({ titleId }: { titleId: string }) => (
  <Box sx={{ p: 1, maxWidth: 380 }}>
    <Typography id={titleId} variant="subtitle2" component="h2" sx={{ mb: 1.5, fontWeight: 600 }}>
      Advanced searching
    </Typography>
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 2.25,
        typography: 'body2',
        '& li': { mb: 1 },
        '& strong': { fontWeight: 600 },
      }}
    >
      <li>
        <strong>First or Last name by field:</strong> use{' '}
        <Box component="code" sx={{ ...codeSx, whiteSpace: 'nowrap' }}>
          field:value
        </Box>
        , e.g. last name contains{' '}
        <Box component="code" sx={{ ...codeSx, whiteSpace: 'nowrap' }}>
          last_name:Perez
        </Box>
        , or first name is equal to{' '}
        <Box component="code" sx={{ ...codeSx, whiteSpace: 'nowrap' }}>
          first_name:&apos;juan&apos;
        </Box>
        .
      </li>
      <li>
        <strong>Chain several criteria:</strong> separate clauses with spaces in one query, e.g.{' '}
        <Box
          component="code"
          sx={{
            ...codeSx,
            display: 'block',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            mt: 0.5,
          }}
        >
          first_name: &apos;Juan&apos; last_name: &apos;Perez&apos; email:@gmail.com
        </Box>
      </li>
      <li>
        <strong>Email (contains):</strong> use a domain or local part (e.g.{' '}
        <Box component="code" sx={codeSx}>
          email:@turn.ai
        </Box>{' '}
        or{' '}
        <Box component="code" sx={{ ...codeSx, whiteSpace: 'nowrap' }}>
          email:support
        </Box>
        ).
      </li>
      <li>
        <strong>Email (exact):</strong> enter the full address in quotes (e.g.{' '}
        <Box component="code" sx={{ ...codeSx, whiteSpace: 'nowrap' }}>
          email:&quot;support@turn.ai&quot;
        </Box>
        ).
      </li>
      <li>
        <strong>Phone number:</strong> partial digits (e.g.{' '}
        <Box component="code" sx={{ ...codeSx, whiteSpace: 'nowrap' }}>
          phone_number:555
        </Box>
        ) or exact in quotes (e.g.{' '}
        <Box component="code" sx={{ ...codeSx, whiteSpace: 'nowrap' }}>
          phone_number:&apos;5551234567&apos;
        </Box>
        ).
      </li>
      <li>
        <strong>Turn ID:</strong> id fragment (e.g.{' '}
        <Box component="code" sx={{ ...codeSx, whiteSpace: 'nowrap' }}>
          turn_id:C109
        </Box>
        ) or exact (e.g.{' '}
        <Box component="code" sx={{ ...codeSx, whiteSpace: 'nowrap' }}>
          turn_id:&apos;C1097828970&apos;
        </Box>
        ).
      </li>
    </Box>
  </Box>
);

const SUBMIT_ANIM_MS = { enter: 280, exit: 220 };

const PILL_RADIUS = 9999;

export type FilterSearchOnButtonPressProps = {
  /** Filter param key sent to the data provider (default: search query `q`). */
  source?: string;
  /** Shown as the input placeholder (default: "Search"). */
  label?: string;
  /** Required by `<Filter>` so this control stays visible when using `alwaysOn`. */
  alwaysOn?: boolean;
  size?: 'small' | 'medium';
  /** When true, shows the help icon and advanced search popover (e.g. Candidates list only). */
  showSearchHelp?: boolean;
};

/**
 * Search field that does not update list filters while typing; applies `source` on Search or Enter.
 * Intended as a child of react-admin `<Filter>` (see FilterLiveForm: plain TextInput updates on every keystroke).
 */
const FilterSearchOnButtonPress = ({
  source = 'q',
  label = 'Search',
  alwaysOn: _alwaysOn,
  size = 'small',
  showSearchHelp = false,
}: FilterSearchOnButtonPressProps) => {
  const theme = useTheme();
  const accent = TURN_STYLES.primaryAccentColor;
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const appliedRaw = filterValues?.[source];
  const applied = appliedRaw === undefined || appliedRaw === null ? '' : String(appliedRaw);

  const [draft, setDraft] = useState(applied);

  useEffect(() => {
    setDraft(applied);
  }, [applied]);

  const draftTrimmed = draft.trim();
  const appliedTrimmed = applied.trim();
  /** Show Search only when there is text and it differs from the last applied search. */
  const showSubmit = draftTrimmed !== '' && draftTrimmed !== appliedTrimmed;

  const apply = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed === '') return;
    const next = { ...filterValues } as Record<string, unknown>;
    next[source] = trimmed;
    setFilters(next, displayedFilters, false);
  }, [draft, filterValues, setFilters, displayedFilters, source]);

  const handleChange = (value: string) => {
    setDraft(value);
    if (value.trim() !== '') return;
    const current = filterValues?.[source];
    if (current == null || String(current).trim() === '') return;
    const next = { ...filterValues } as Record<string, unknown>;
    delete next[source];
    setFilters(next, displayedFilters, false);
  };

  const borderRadiusBase = theme.shape.borderRadius;
  const innerButtonRadius = typeof borderRadiusBase === 'number' ? borderRadiusBase * 1.5 : borderRadiusBase;
  const helpBaseId = useId();
  const helpTitleId = `${helpBaseId}-title`;
  const helpRegionId = `${helpBaseId}-region`;
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
  const helpOpen = Boolean(helpAnchor);
  const [searchFieldExpanded, setSearchFieldExpanded] = useState(false);
  const searchAreaRef = useRef<HTMLDivElement>(null);

  const handleSearchFocus = useCallback(() => {
    setSearchFieldExpanded(true);
  }, []);

  const handleSearchBlur = useCallback((e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { relatedTarget } = e;
    if (relatedTarget instanceof Node && searchAreaRef.current?.contains(relatedTarget)) return;
    setSearchFieldExpanded(false);
  }, []);

  /** Grow with typed query (sm+); caps avoid dominating the toolbar. */
  const searchInnerWidthSm = useMemo(() => {
    const sidePad = 0;
    const searchBtnExtra = showSubmit ? 0 : 0;
    const charPx = 9;
    const cap = 720;
    const contentNeed = sidePad + draft.length * charPx + searchBtnExtra;
    const floor = 280;
    return Math.min(cap, Math.max(floor, contentNeed));
  }, [draft.length, showSubmit, searchFieldExpanded]);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flex: { xs: '1 1 100%', sm: '0 0 auto' },
        width: { xs: '100%', sm: 'auto' },
        alignSelf: { xs: 'stretch', sm: 'flex-start' },
        alignItems: 'flex-end',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: 0,
        maxWidth: '100%',
      }}
    >
      <Box
        ref={searchAreaRef}
        sx={{
          flexGrow: 0,
          flexShrink: 0,
          width: { xs: '100%', sm: searchInnerWidthSm },
          maxWidth: { xs: '100%', sm: 'min(720px, 92vw)' },
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.short,
            easing: theme.transitions.easing.easeInOut,
          }),
        }}
      >
        <TextField
          fullWidth
          placeholder={label}
          inputProps={{ 'aria-label': label }}
          size={size}
          value={draft}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (showSubmit) apply();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              width: '100%',
              borderRadius: PILL_RADIUS,
              paddingLeft: theme.spacing(2),
              paddingRight: showSubmit ? theme.spacing(0.5) : theme.spacing(2),
              paddingTop: theme.spacing(0.5),
              paddingBottom: theme.spacing(0.5),
              alignItems: 'center',
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
              transition: theme.transitions.create('padding-right', {
                duration: showSubmit ? SUBMIT_ANIM_MS.enter : SUBMIT_ANIM_MS.exit,
                easing: theme.transitions.easing.easeInOut,
              }),
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(accent, 0.45),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(accent, 0.75),
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: accent,
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: theme.spacing(1, 0.5, 1, 0),
              minHeight: size === 'small' ? 20 : 24,
              overflowX: 'auto',
            },
            '& .MuiInputAdornment-root': {
              margin: 0,
              maxHeight: 'none',
              height: 'auto',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ margin: 0, maxHeight: 'none' }}>
                <Collapse
                  orientation="horizontal"
                  in={showSubmit}
                  collapsedSize={0}
                  timeout={{ enter: SUBMIT_ANIM_MS.enter, exit: SUBMIT_ANIM_MS.exit }}
                  sx={{
                    '& .MuiCollapse-wrapper': { flexDirection: 'row' },
                    '& .MuiCollapse-wrapperInner': { display: 'flex', alignItems: 'center' },
                  }}
                >
                  <Zoom
                    in={showSubmit}
                    timeout={{ enter: SUBMIT_ANIM_MS.enter, exit: SUBMIT_ANIM_MS.exit }}
                    style={{ transformOrigin: '100% 50%' }}
                  >
                    <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                      <Button
                        type="button"
                        disableElevation
                        variant="contained"
                        size={size}
                        onClick={apply}
                        sx={{
                          minWidth: 88,
                          px: 1.75,
                          py: size === 'small' ? 0.75 : 1,
                          borderRadius: innerButtonRadius,
                          boxShadow: 'none',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
                          fontWeight: 600,
                          backgroundColor: accent,
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: accent,
                            filter: 'brightness(0.92)',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        Search
                      </Button>
                    </Box>
                  </Zoom>
                </Collapse>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {showSearchHelp ? (
        <>
          <Tooltip title="Search help" enterDelay={400}>
            <IconButton
              type="button"
              size="small"
              aria-label="Search help"
              aria-expanded={helpOpen}
              aria-controls={helpRegionId}
              onClick={(e) => setHelpAnchor(helpOpen ? null : e.currentTarget)}
              sx={{
                color: TURN_STYLES.primaryAccentColor,
                flexShrink: 0,
                ml: 0.25,
                p: '4px',
                mb: 0.5,
                '&:hover': {
                  color: TURN_STYLES.primaryAccentColor,
                  bgcolor: alpha(TURN_STYLES.primaryAccentColor, 0.08),
                },
              }}
            >
              <HelpOutlineIcon fontSize="small" titleAccess="" />
            </IconButton>
          </Tooltip>
          <Popover
            id={helpRegionId}
            open={helpOpen}
            anchorEl={helpAnchor}
            onClose={() => setHelpAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                'aria-labelledby': helpTitleId,
                elevation: 4,
                sx: { mt: 0.5, maxWidth: 'min(380px, calc(100vw - 24px))' },
              },
            }}
          >
            <AdvancedSearchHelp titleId={helpTitleId} />
          </Popover>
        </>
      ) : null}
    </Box>
  );
};

export default FilterSearchOnButtonPress;
